import { useEffect, useRef, useState } from 'react';
import { kr } from '../stats.js';

/**
 * A number field that saves when the user leaves it (or presses Enter),
 * not on every keystroke — one Cosmos write per edit instead of one per digit.
 */
export function NumberInput({ value, onCommit, className = 'num', style, min = 0, ...rest }) {
  const [draft, setDraft] = useState(String(value ?? 0));
  useEffect(() => { setDraft(String(value ?? 0)); }, [value]);

  const commit = () => {
    const n = Math.max(min, parseInt(draft || '0', 10) || 0);
    setDraft(String(n));
    if (n !== Number(value ?? 0)) onCommit(n);
  };

  return (
    <input
      type="number" inputMode="numeric" min={min}
      className={className} style={style}
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onFocus={e => e.target.select()}
      onBlur={commit}
      onKeyDown={e => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      {...rest}
    />
  );
}

export function Kpi({ label, value, note, mutedNote, soft, style, labelClass }) {
  return (
    <div className={'kpi' + (soft ? ' soft' : '')} style={style}>
      <div className={'kpi-label' + (labelClass ? ' ' + labelClass : '')}>{label}</div>
      <div className="kpi-value">{value}</div>
      {note && <div className={"kpi-note" + (mutedNote ? " muted-note" : "")}>{note}</div>}
    </div>
  );
}

export function SectionLabel({ children }) {
  return (
    <div className="section-label"><div>{children}</div><div className="rule" /></div>
  );
}

export function Bar({ pct }) {
  return <div className="bar"><div style={{ width: `${pct}%` }} /></div>;
}

export function YearSelect({ years, year, onChange, className = 'year-select' }) {
  return (
    <select className={className} value={year ?? ''} onChange={e => onChange(Number(e.target.value))}>
      {years.map(y => <option key={y} value={y}>{y}</option>)}
    </select>
  );
}

export function Toast({ toast }) {
  if (!toast) return null;
  return <div className={'toast' + (toast.error ? ' err' : '')}>{toast.message}</div>;
}

export function Spinner({ label }) {
  return <div className="center"><div className="spinner" />{label && <div>{label}</div>}</div>;
}

/**
 * Sold / allocation / retur for one player and one lottery type, as a table
 * cell. − and + step the count; quick clicks are gathered and saved as one
 * number once the clicking stops, so no step gets lost on the way.
 */
export function SoldCell({ cell, onCommit, small }) {
  const [pending, setPending] = useState(null);   // stepped but not yet saved
  const latest = useRef(null);
  const timer = useRef(null);
  const commit = useRef(onCommit);
  commit.current = onCommit;

  const flush = () => {
    clearTimeout(timer.current);
    const v = latest.current;
    latest.current = null;
    setPending(null);
    if (v != null) commit.current(v);
  };
  useEffect(() => () => { clearTimeout(timer.current); if (latest.current != null) commit.current(latest.current); }, []);

  const step = d => {
    const base = latest.current ?? cell.value;
    const v = Math.max(0, base + d);
    if (v === base) return;
    latest.current = v;
    setPending(v);
    clearTimeout(timer.current);
    timer.current = setTimeout(flush, 600);
  };

  const typed = v => {
    clearTimeout(timer.current);
    latest.current = null;
    setPending(null);
    onCommit(v);
  };

  const shown = pending ?? cell.value;
  const ret = Math.max(0, cell.alloc - shown);

  return (
    <div className={'sold-cell' + (small ? ' small' : '')}>
      <div className="stepper">
        <button type="button" className="step" onClick={() => step(-1)} disabled={shown <= 0}
          aria-label={`En ${cell.name} mindre`} title="En mindre">−</button>
        <NumberInput
          value={shown} onCommit={typed}
          style={small ? { width: 44, padding: 5, fontSize: 13, borderRadius: 7 } : { width: 50 }}
          aria-label={`Sålda ${cell.name}`}
        />
        <button type="button" className="step" onClick={() => step(1)}
          aria-label={`En ${cell.name} till`} title="En till">+</button>
        <span className="of">/ {cell.alloc}</span>
      </div>
      {/* Hidden, not removed, at 0 so the fields don't shift when it appears. */}
      <span className="ret" style={ret > 0 ? undefined : { visibility: 'hidden' }} aria-hidden={ret === 0}>
        Retur {ret}
      </span>
    </div>
  );
}

/** The "Betalt" tick box for one player. */
export function PaidBox({ player, onFlags, label }) {
  return (
    <label className={'paid-box' + (player.paid ? ' on' : '')}>
      <input type="checkbox" checked={player.paid}
        onChange={e => onFlags(player.id, { paid: e.target.checked })}
        aria-label={`${player.name} har betalt`} />
      {label}
    </label>
  );
}

/**
 * Player table used by the leader view and the admin team modal. With
 * `onRemove` each row gets a button that takes the player out of the team.
 */
export function PlayerTable({ team, types, onSold, onFlags, onRemove, compact }) {
  const extra = onRemove ? 1 : 0;
  return (
    <table className="t" style={{ minWidth: compact ? 900 : 980 }}>
      <thead>
        <tr>
          <th className="l" style={{ padding: compact ? '11px 14px' : '13px 16px', ...(compact ? stickyTh : {}) }}>Spelare</th>
          {types.map(t => <th key={t.id} style={compact ? stickyTh : { padding: '13px 8px' }}>{t.name}</th>)}
          <th className="r" style={{ padding: '13px 14px', ...(compact ? stickyTh : {}) }}>Att betala</th>
          <th style={{ padding: '13px 14px', ...(compact ? stickyTh : {}) }}>Betalt</th>
          {onRemove && <th style={{ width: 52, ...(compact ? stickyTh : {}) }} aria-label="Ta bort" />}
        </tr>
      </thead>
      <tbody>
        {team.players.map(p => (
          <tr key={p.id} className={p.paid ? 'paid' : ''}>
            <td className="name" style={{ padding: compact ? '9px 14px' : '10px 16px', fontSize: compact ? 14 : 14.5 }}>{p.name}</td>
            {p.cells.map(c => (
              <td key={c.type} style={{ textAlign: 'center', padding: compact ? '8px 16px' : '8px 14px' }}>
                <SoldCell cell={c} small={compact} onCommit={v => onSold(p.id, c.type, v)} />
              </td>
            ))}
            <td className="num-r" style={{ padding: '8px 14px', fontSize: 14 }}>{kr(p.toPay)}</td>
            <td style={{ textAlign: 'center', padding: '8px 14px' }}><PaidBox player={p} onFlags={onFlags} /></td>
            {onRemove && (
              <td style={{ textAlign: 'center', padding: '8px 10px 8px 0' }}>
                <button type="button" className="icon-btn row-remove" onClick={() => onRemove(p)}
                  title={`Ta bort ${p.name}`} aria-label={`Ta bort ${p.name}`}>✕</button>
              </td>
            )}
          </tr>
        ))}
        {!team.players.length && (
          <tr><td colSpan={types.length + 3 + extra} className="empty">Inga spelare i laget ännu.</td></tr>
        )}
        {team.players.length > 0 && (
          <tr className="sum-row">
            <td style={{ padding: compact ? '10px 14px' : '11px 16px' }}>Totalt</td>
            {types.map(t => <td key={t.id} />)}
            <td className="num-r" style={{ padding: '10px 14px' }}>{kr(team.toPay)}</td>
            <td className="num-c muted" style={{ padding: '10px 14px', fontSize: 12.5 }}>{team.paidCount} / {team.players.length}</td>
            {onRemove && <td />}
          </tr>
        )}
      </tbody>
    </table>
  );
}

const stickyTh = { position: 'sticky', top: 0, background: 'var(--bg)', padding: '11px 8px' };

/**
 * The app's confirm/prompt dialog, opened through actions.confirm and
 * actions.prompt. `dialog` is { title, message, okLabel, danger, input, resolve };
 * with `input` ({ label, placeholder, type, value, validate }) it asks for a value.
 */
export function Dialog({ dialog, onClose }) {
  const [value, setValue] = useState('');
  const [error, setError] = useState('');
  useEffect(() => { setValue(dialog?.input?.value || ''); setError(''); }, [dialog]);

  useEffect(() => {
    if (!dialog) return undefined;
    /* Capture phase, so Escape closes only this dialog and not a modal under it. */
    const onKey = e => {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onClose(null);
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [dialog, onClose]);

  if (!dialog) return null;
  const { title, message, okLabel = 'OK', cancelLabel = 'Avbryt', danger, input } = dialog;

  const submit = e => {
    e.preventDefault();
    if (!input) return onClose(true);
    const problem = input.validate?.(value);
    if (problem) return setError(problem);
    return onClose(value);
  };

  return (
    <div className="overlay dialog-overlay" onClick={() => onClose(null)}>
      <form className="modal dialog" role="dialog" aria-modal="true" aria-labelledby="dialog-title"
        onClick={e => e.stopPropagation()} onSubmit={submit}>
        <h2 id="dialog-title" className="title">{title}</h2>
        {message && <div className="dialog-msg">{message}</div>}
        {input && (
          <div className="field" style={{ marginTop: 14 }}>
            {input.label && <label className="label" htmlFor="dialog-input">{input.label}</label>}
            <input id="dialog-input" className="input" autoFocus type={input.type || 'text'}
              placeholder={input.placeholder} value={value}
              onChange={e => { setValue(e.target.value); setError(''); }} />
            {error && <div className="dialog-err">{error}</div>}
          </div>
        )}
        <div className="dialog-actions">
          <button type="button" className="btn-ghost" onClick={() => onClose(null)}>{cancelLabel}</button>
          <button type="submit" className={'btn' + (danger ? ' btn-bad' : '')} autoFocus={!input}>{okLabel}</button>
        </div>
      </form>
    </div>
  );
}

/**
 * A list the user reorders by dragging the ⋮⋮ handle — pointer events, so it
 * works with mouse, touch and pen alike. The handle also takes ↑/↓ from the
 * keyboard. onReorder gets the item ids in their new order.
 */
export function SortableList({ items, onReorder, renderItem, rowClass }) {
  const [drag, setDrag] = useState(null);      // { from, to } — `to` is an insertion index 0…n
  const rows = useRef([]);

  const move = (from, to) => {
    const ids = items.map(i => i.id);
    const [id] = ids.splice(from, 1);
    ids.splice(to > from ? to - 1 : to, 0, id);
    if (ids.some((x, i) => x !== items[i].id)) onReorder(ids);
  };

  const insertionAt = y => {
    const i = rows.current.slice(0, items.length).findIndex(el => {
      const r = el?.getBoundingClientRect();
      return r && y < r.top + r.height / 2;
    });
    return i < 0 ? items.length : i;
  };

  const handlers = i => ({
    onPointerDown: e => {
      if (e.button !== 0) return;
      e.preventDefault();
      e.currentTarget.setPointerCapture(e.pointerId);
      setDrag({ from: i, to: i });
    },
    onPointerMove: e => {
      if (!drag) return;
      if (e.clientY < 60) window.scrollBy(0, -12);
      else if (e.clientY > window.innerHeight - 60) window.scrollBy(0, 12);
      const to = insertionAt(e.clientY);
      if (to !== drag.to) setDrag({ ...drag, to });
    },
    onPointerUp: () => {
      if (drag) move(drag.from, drag.to);
      setDrag(null);
    },
    onPointerCancel: () => setDrag(null),
    onKeyDown: e => {
      if (e.key === 'ArrowUp' && i > 0) { e.preventDefault(); move(i, i - 1); }
      if (e.key === 'ArrowDown' && i < items.length - 1) { e.preventDefault(); move(i, i + 2); }
    },
  });

  /* Show the drop line only where dropping would change something. */
  const line = drag && drag.to !== drag.from && drag.to !== drag.from + 1 ? drag.to : -1;

  return items.map((item, i) => (
    <div key={item.id} ref={el => { rows.current[i] = el; }}
      className={['list-row sortable-row', rowClass?.(item),
        drag?.from === i && 'dragging',
        line === i && 'drop-before',
        line === items.length && i === items.length - 1 && 'drop-after'].filter(Boolean).join(' ')}>
      <button type="button" className="drag-handle" aria-label={`Flytta ${item.name ?? ''} (pil upp/ned)`}
        title="Dra för att ändra ordning" {...handlers(i)}>⋮⋮</button>
      {renderItem(item, i)}
    </div>
  ));
}
